import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, MapPin, Calendar, Users, Star, CheckCircle2, Clock, BookOpen, Menu, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';

const API = window.location.origin + '/api';

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [enquiryForm, setEnquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    year_level: 5,
    subjects: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API}/subjects`);
      const uniqueSubjects = [...new Set(response.data.map(s => s.name))];
      setSubjects(uniqueSubjects);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const handleEnquiry = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, {
        name: enquiryForm.name,
        email: enquiryForm.email,
        phone: enquiryForm.phone,
        year_level: enquiryForm.year_level,
        message: `Subjects needed: ${enquiryForm.subjects || 'Not specified'}. ${enquiryForm.message}`
      });
      toast.success('Thanks! We\'ll get back to you within 24 hours.');
      setEnquiryForm({ name: '', email: '', phone: '', year_level: 5, subjects: '', message: '' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen noise-bg">
      {/* Navigation */}
      <nav className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
              <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
            </svg>
            <span className="flex flex-col leading-tight">
              <span className="text-2xl font-bold text-foreground">Igniting Minds</span>
              <span className="text-xs font-medium text-muted-foreground tracking-wide">Academic Architect</span>
            </span>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-foreground hover:text-primary transition-colors">Features</a>
            <a href="#pricing" className="text-foreground hover:text-primary transition-colors">Pricing</a>
            <a href="#location" className="text-foreground hover:text-primary transition-colors">Location</a>
            <a href="tel:0472555357" className="flex items-center gap-2 text-foreground hover:text-primary transition-colors">
              <Phone className="w-4 h-4" />
              0472 555 357
            </a>
            <Link to="/book">
              <Button data-testid="nav-book-now-btn" className="btn-primary">Book Now</Button>
            </Link>
          </div>

          <button 
            data-testid="mobile-menu-toggle"
            className="md:hidden text-foreground"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-border px-6 py-4 space-y-4">
            <a href="#features" className="block text-foreground hover:text-primary" onClick={() => setMobileMenuOpen(false)}>Features</a>
            <a href="#pricing" className="block text-foreground hover:text-primary" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <a href="#location" className="block text-foreground hover:text-primary" onClick={() => setMobileMenuOpen(false)}>Location</a>
            <a href="tel:0472555357" className="flex items-center gap-2 text-foreground hover:text-primary">
              <Phone className="w-4 h-4" />
              0472 555 357
            </a>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden section-padding">
        <div className="blob-bg" style={{background: 'hsl(var(--accent))', top: '-200px', right: '-200px'}} />
        
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10">
          <motion.div {...fadeInUp}>
            <div className="flex items-center gap-2 mb-4">
              <div className="flex -space-x-2">
                {[1,2,3].map(i => (
                  <img 
                    key={i}
                    src={`https://images.unsplash.com/photo-${i === 1 ? '1438761681033-6461ffad8d80' : i === 2 ? '1500648767791-00dcc994a43e' : '1534528741775-53994a69daeb'}?w=40&h=40&fit=crop`}
                    alt="Student"
                    className="w-10 h-10 rounded-full border-2 border-white"
                  />
                ))}
              </div>
              <span className="text-sm text-muted-foreground">Trusted by families in Mosman Park</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{fontFamily: 'Fraunces, serif'}}>
              <span className="text-primary">Result-Oriented</span> Academic Services for Year 1–10 Students
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground mb-8">
              Qualified educator crafting personalized learning strategies. Expert academic services including tutoring and scholarship eligibility testing.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Link to="/book">
                <Button data-testid="hero-book-now-btn" className="btn-primary w-full sm:w-auto">Book Your Spot Now</Button>
              </Link>
              <a href="tel:0472555357">
                <Button data-testid="hero-call-btn" variant="outline" className="btn-secondary w-full sm:w-auto">
                  <Phone className="w-4 h-4 mr-2" />
                  Call 0472 555 357
                </Button>
              </a>
            </div>

            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-success" />
                <span>Limited Spots</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-warning fill-warning" />
                <span>5★ Reviews</span>
              </div>
            </div>
          </motion.div>

          <motion.div 
            {...fadeInUp}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="relative"
          >
            <img 
              src="https://images.pexels.com/photos/4173338/pexels-photo-4173338.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Qualified educator helping a young student with academics"
              className="rounded-3xl shadow-2xl w-full max-h-[60vh] object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{fontFamily: 'Fraunces, serif'}}>Why Choose Igniting Minds?</h2>
            <p className="text-lg text-muted-foreground">Strategic academic services delivering measurable outcomes</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: CheckCircle2, title: 'Qualified Educator', desc: 'Certified educator with years of experience in WA curriculum', color: 'text-success' },
              { icon: Star, title: 'Proven Results', desc: 'Track record of improved grades and confident learners', color: 'text-warning' },
              { icon: BookOpen, title: 'Study Planning', desc: 'Personalized learning plans tailored to each student', color: 'text-primary' },
              { icon: Users, title: 'Flexible Options', desc: '1-on-1, pairs, or small groups to suit your needs', color: 'text-secondary' },
              { icon: Clock, title: 'Convenient Times', desc: 'Weekdays 4–8 PM, flexible scheduling available', color: 'text-accent' },
              { icon: MapPin, title: 'Great Location', desc: 'Easy to reach in Mosman Park, Perth WA', color: 'text-primary' }
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card data-testid={`feature-card-${i}`} className="card-feature">
                  <feature.icon className={`w-12 h-12 ${feature.color} mb-4`} />
                  <h3 className="text-xl font-semibold mb-2" style={{fontFamily: 'Fraunces, serif'}}>{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="section-padding">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{fontFamily: 'Fraunces, serif'}}>Simple, Clear Pricing</h2>
            <p className="text-lg text-muted-foreground">Choose the option that works best for you</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { type: '1-on-1 Session', price: 90, students: '1 student', popular: true, desc: 'Personalized attention for maximum results' },
              { type: 'Pair Session', price: 150, students: '2 students', popular: false, desc: 'Learn together with a friend' },
              { type: 'Small Group', price: 200, students: 'Up to 5 students', popular: false, desc: 'Collaborative learning environment' }
            ].map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card data-testid={`pricing-card-${i}`} className={`card-pricing ${plan.popular ? 'border-primary' : ''}`}>
                  {plan.popular && (
                    <div className="absolute top-4 right-4 bg-primary text-white text-xs px-3 py-1 rounded-full">
                      Most Popular
                    </div>
                  )}
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold mb-2" style={{fontFamily: 'Fraunces, serif'}}>{plan.type}</h3>
                    <div className="flex items-baseline justify-center gap-1 mb-2">
                      <span className="text-5xl font-bold" style={{fontFamily: 'JetBrains Mono, monospace'}}>${plan.price}</span>
                      <span className="text-muted-foreground">/hour</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{plan.students}</p>
                  </div>
                  <p className="text-center mb-6 text-muted-foreground">{plan.desc}</p>
                  <Link to="/book" className="block">
                    <Button data-testid={`pricing-book-btn-${i}`} className="btn-primary w-full">
                      Book {plan.type}
                    </Button>
                  </Link>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Schedule & Location Section */}
      <section id="location" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{fontFamily: 'Fraunces, serif'}}>Our Schedule & Location</h2>
            
            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <Clock className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">Weekday Sessions</h3>
                  <p className="text-muted-foreground">Monday to Friday, 4:00 PM – 8:00 PM</p>
                  <p className="text-sm text-muted-foreground mt-1">Book online anytime</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <Calendar className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">Weekend Sessions</h3>
                  <p className="text-muted-foreground">Available by special arrangement</p>
                  <p className="text-sm text-muted-foreground mt-1">Use the enquiry form below</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <MapPin className="w-6 h-6 text-success flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">Location</h3>
                  <p className="text-muted-foreground">Mosman Park, Perth WA</p>
                  <a href="tel:0472555357" className="inline-flex items-center gap-2 text-primary hover:underline mt-2">
                    <Phone className="w-4 h-4" />
                    0472 555 357
                  </a>
                </div>
              </div>
            </div>

            <img 
              src="https://images.pexels.com/photos/28458386/pexels-photo-28458386.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Igniting Minds location near Mosman Park Perth"
              className="rounded-2xl shadow-lg w-full"
            />
          </motion.div>

          {/* Enquiry Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <Card data-testid="enquiry-form" className="card-feature">
              <h3 className="text-2xl font-bold mb-6" style={{fontFamily: 'Fraunces, serif'}}>Weekend Enquiry Form</h3>
              <form onSubmit={handleEnquiry} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Your Name</label>
                  <Input
                    data-testid="enquiry-name-input"
                    className="input-field"
                    value={enquiryForm.name}
                    onChange={(e) => setEnquiryForm({...enquiryForm, name: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <Input
                    data-testid="enquiry-email-input"
                    type="email"
                    className="input-field"
                    value={enquiryForm.email}
                    onChange={(e) => setEnquiryForm({...enquiryForm, email: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <Input
                    data-testid="enquiry-phone-input"
                    type="tel"
                    className="input-field"
                    value={enquiryForm.phone}
                    onChange={(e) => setEnquiryForm({...enquiryForm, phone: e.target.value})}
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Year Level</label>
                    <select
                      data-testid="enquiry-year-select"
                      className="input-field w-full"
                      value={enquiryForm.year_level}
                      onChange={(e) => setEnquiryForm({...enquiryForm, year_level: parseInt(e.target.value)})}
                    >
                {[1,2,3,4,5,6,7,8,9,10].map(y => <option key={y} value={y}>Year {y}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Subjects Needed</label>
                    <Input
                      data-testid="enquiry-subjects-input"
                      className="input-field"
                      value={enquiryForm.subjects}
                      onChange={(e) => setEnquiryForm({...enquiryForm, subjects: e.target.value})}
                      placeholder="e.g., Math, English"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Message</label>
                  <Textarea
                    data-testid="enquiry-message-textarea"
                    className="input-field min-h-[120px]"
                    value={enquiryForm.message}
                    onChange={(e) => setEnquiryForm({...enquiryForm, message: e.target.value})}
                    required
                  />
                </div>
                
                <Button 
                  data-testid="enquiry-submit-btn"
                  type="submit" 
                  className="btn-primary w-full"
                  disabled={submitting}
                >
                  {submitting ? 'Sending...' : 'Send Enquiry'}
                </Button>
              </form>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{fontFamily: 'Fraunces, serif'}}>What Parents Say</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: 'Sarah M.', text: 'My daughter\'s confidence in math has skyrocketed! Grades improved from C to A in just 2 terms.', rating: 5 },
              { name: 'James P.', text: 'Fantastic educator who really understands how to engage kids. Worth every dollar.', rating: 5 },
              { name: 'Lisa K.', text: 'Flexible scheduling and personalized approach. Our son actually looks forward to sessions now!', rating: 5 }
            ].map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card data-testid={`testimonial-card-${i}`} className="card-feature">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, j) => (
                      <Star key={j} className="w-5 h-5 text-warning fill-warning" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-4 italic" style={{fontFamily: 'Fraunces, serif'}}>"{testimonial.text}"</p>
                  <p className="font-semibold">– {testimonial.name}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-primary to-accent text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6" style={{fontFamily: 'Fraunces, serif'}}>Ready to See Results?</h2>
            <p className="text-xl mb-8 opacity-90">Limited spots available. Book your session today!</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button data-testid="cta-book-btn" className="bg-white text-primary hover:bg-white/90 btn-primary text-lg px-12 py-6">
                  Secure Your Spot
                </Button>
              </Link>
              <a href="tel:0472555357">
                <Button data-testid="cta-call-btn" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary btn-secondary text-lg px-12 py-6">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-secondary text-white py-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <BookOpen className="w-8 h-8" />
            <span className="flex flex-col leading-tight items-center">
              <span className="text-2xl font-bold" style={{fontFamily: 'Fraunces, serif'}}>Igniting Minds</span>
              <span className="text-xs font-medium text-white/60 tracking-wide">Academic Architect</span>
            </span>
          </div>
          <p className="text-white/80 mb-4">Mosman Park, Perth WA | <a href="https://ignitingminds.com.au" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">ignitingminds.com.au</a></p>
          <a href="tel:0472555357" className="text-white hover:text-accent transition-colors">
            0472 555 357
          </a>
          <p className="text-white/60 text-sm mt-6">© 2026 Igniting Minds. All rights reserved.</p>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className="sticky-bottom-cta md:hidden">
        <Link to="/book" className="block">
          <Button data-testid="mobile-sticky-book-btn" className="btn-primary w-full text-lg py-6">
            Book Your Session Now
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default LandingPage;
